import { useEffect, useMemo, useState } from 'react';
import { Header } from '@/components/Header';
import { useAppStore } from '@/stores/appStore';
import {
  getInstantlyCampaignDetail,
  listInstantlyCampaignOptions,
  type InstantlyCampaignOption,
  updateInstantlyCampaign,
} from '@/services/research';

type StepRow = {
  id: string;
  subject: string;
  body: string;
  delayDays: number;
};

type CampaignSnapshot = {
  id: string;
  name: string;
  timezone: string;
  startDate: string;
  endDate: string;
  dailyLimit: string;
  emailGapMinutes: string;
  stopOnReply: boolean;
  openTracking: boolean;
  linkTracking: boolean;
  steps: StepRow[];
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function asArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map((row) => asRecord(row)).filter((row) => Object.keys(row).length > 0) : [];
}

function firstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
}

function toBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lowered = value.trim().toLowerCase();
    if (lowered === 'true') return true;
    if (lowered === 'false') return false;
  }
  if (typeof value === 'number') return value !== 0;
  return fallback;
}

function toIntString(value: unknown, fallback: string): string {
  const num = Number(value);
  return Number.isFinite(num) ? String(Math.max(0, Math.floor(num))) : fallback;
}

function extractDelayDays(step: Record<string, unknown>, variant: Record<string, unknown>): number {
  const timing = asRecord(step.timing);
  const variantTiming = asRecord(variant.timing);
  const candidates: unknown[] = [
    step.delay_days,
    step.delayDays,
    step.wait_days,
    step.waitDays,
    step.days_before_sending,
    step.daysBeforeSending,
    step.delay,
    timing.delay_days,
    timing.wait_days,
    timing.days,
    variant.delay_days,
    variant.delayDays,
    variant.wait_days,
    variant.waitDays,
    variant.delay,
    variantTiming.delay_days,
    variantTiming.wait_days,
    variantTiming.days,
  ];
  for (const candidate of candidates) {
    const num = Number(candidate);
    if (Number.isFinite(num)) return Math.max(0, Math.floor(num));
  }
  return 0;
}

function decodeHtmlEntities(value: string): string {
  if (typeof window === 'undefined') return value;
  const el = document.createElement('textarea');
  el.innerHTML = value;
  return el.value;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

function toPreviewHtml(value: string): string {
  const normalized = decodeHtmlEntities(String(value ?? '').trim());
  if (!normalized) return '';
  if (looksLikeHtml(normalized)) return normalized;
  const lines = normalized.replace(/\r\n/g, '\n').split('\n');
  return lines
    .map((line) => (line.trim() ? `<div>${escapeHtml(line)}</div>` : '<div><br /></div>'))
    .join('');
}

function normalizeStep(step: Record<string, unknown>, index: number): StepRow {
  const variants = asArray(step.variants);
  const firstVariant = variants[0] ?? {};
  return {
    id: `step-${index + 1}`,
    subject: firstNonEmptyString(step.subject, firstVariant.subject),
    body: firstNonEmptyString(step.body, step.body_text, firstVariant.body, firstVariant.body_text),
    delayDays: extractDelayDays(step, firstVariant),
  };
}

function parseCampaignSnapshot(option: InstantlyCampaignOption, rawInput?: Record<string, unknown>): CampaignSnapshot {
  const raw = asRecord(rawInput);
  const sequences = asArray(raw.sequences);
  const firstSequence = asRecord(sequences[0]);
  const rawSteps = asArray(firstSequence.steps);
  const parsedSteps = rawSteps.map(normalizeStep).filter((step) => step.subject || step.body);
  return {
    id: option.id,
    name: firstNonEmptyString(raw.name, option.name),
    timezone: firstNonEmptyString(raw.timezone, raw.time_zone, 'America/Chicago'),
    startDate: firstNonEmptyString(raw.start_date, raw.startDate),
    endDate: firstNonEmptyString(raw.end_date, raw.endDate),
    dailyLimit: toIntString(raw.daily_limit ?? raw.dailyLimit, '50'),
    emailGapMinutes: toIntString(raw.email_gap ?? raw.emailGap, '5'),
    stopOnReply: toBool(raw.stop_on_reply ?? raw.stopOnReply, true),
    openTracking: toBool(raw.open_tracking ?? raw.openTracking, true),
    linkTracking: toBool(raw.link_tracking ?? raw.linkTracking, true),
    steps: parsedSteps.length > 0 ? parsedSteps : [{ id: 'step-1', subject: '', body: '', delayDays: 0 }],
  };
}

export function EmailCampaignEditor() {
  const userEmail = useAppStore((s) => s.userEmail);
  const [campaigns, setCampaigns] = useState<InstantlyCampaignOption[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<CampaignSnapshot | null>(null);

  useEffect(() => {
    const loadCampaigns = async () => {
      if (!userEmail) return;
      setLoadingCampaigns(true);
      setStatus(null);
      try {
        const result = await listInstantlyCampaignOptions({ userEmail });
        if (!result.success) throw new Error(result.error || 'Failed to load campaigns');
        const options = Array.isArray(result.campaigns) ? result.campaigns : [];
        setCampaigns(options);
        if (!selectedCampaignId && options.length > 0) {
          const defaultId = result.defaultCampaign?.id || options[0].id;
          setSelectedCampaignId(defaultId);
        }
      } catch (error: any) {
        setStatus(error?.message || 'Failed to load campaigns.');
      } finally {
        setLoadingCampaigns(false);
      }
    };
    void loadCampaigns();
  }, [userEmail]);

  useEffect(() => {
    const loadDetail = async () => {
      if (!userEmail || !selectedCampaignId) {
        setSnapshot(null);
        return;
      }
      setLoadingDetail(true);
      try {
        const result = await getInstantlyCampaignDetail({ userEmail, campaignId: selectedCampaignId });
        if (!result.success || !result.campaign) throw new Error(result.error || 'Failed to load campaign detail');
        const option = campaigns.find((row) => row.id === selectedCampaignId) ?? {
          id: selectedCampaignId,
          name: result.campaign.name || selectedCampaignId,
        };
        setSnapshot(parseCampaignSnapshot(option, asRecord(result.campaign.raw)));
      } catch (error: any) {
        setSnapshot(null);
        setStatus(error?.message || 'Failed to load campaign detail.');
      } finally {
        setLoadingDetail(false);
      }
    };
    void loadDetail();
  }, [campaigns, selectedCampaignId, userEmail]);

  const canSave = useMemo(() => {
    if (!snapshot) return false;
    if (!snapshot.name.trim()) return false;
    return snapshot.steps.some((step) => step.subject.trim() && step.body.trim());
  }, [snapshot]);

  const updateSnapshot = (patch: Partial<CampaignSnapshot>) => {
    setSnapshot((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const updateStep = (id: string, patch: Partial<StepRow>) => {
    setSnapshot((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        steps: prev.steps.map((row) => (row.id === id ? { ...row, ...patch } : row)),
      };
    });
  };

  const addStep = () => {
    setSnapshot((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        steps: [...prev.steps, { id: `step-${prev.steps.length + 1}`, subject: '', body: '', delayDays: 0 }],
      };
    });
  };

  const removeStep = (id: string) => {
    setSnapshot((prev) => {
      if (!prev || prev.steps.length <= 1) return prev;
      return {
        ...prev,
        steps: prev.steps.filter((row) => row.id !== id),
      };
    });
  };

  const handleSave = async () => {
    if (!userEmail || !snapshot) return;
    setSaving(true);
    setStatus(null);
    try {
      const result = await updateInstantlyCampaign({
        userEmail,
        campaignId: snapshot.id,
        name: snapshot.name,
        timezone: snapshot.timezone,
        startDate: snapshot.startDate || undefined,
        endDate: snapshot.endDate || undefined,
        dailyLimit: Number(snapshot.dailyLimit) || undefined,
        emailGapMinutes: Number(snapshot.emailGapMinutes) || undefined,
        stopOnReply: snapshot.stopOnReply,
        openTracking: snapshot.openTracking,
        linkTracking: snapshot.linkTracking,
        steps: snapshot.steps.map((step) => ({
          subject: step.subject,
          body: step.body,
          delayDays: step.delayDays,
        })),
      });
      if (!result.success) throw new Error(result.error || 'Campaign update failed');
      setStatus(`Campaign updated: ${snapshot.name} (${snapshot.id})`);
    } catch (error: any) {
      setStatus(error?.message || 'Failed to update campaign.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Campaign Editor</h1>
          <p className="text-gray-400 mt-2">Review and edit live Instantly campaigns and sequence steps.</p>
        </div>

        {status && <div className="rounded border border-gray-700 bg-gray-900/60 px-4 py-3 text-sm text-gray-200">{status}</div>}

        <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-300">Campaign</label>
            <select
              value={selectedCampaignId}
              onChange={(e) => setSelectedCampaignId(e.target.value)}
              className="min-w-[320px] px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              disabled={loadingCampaigns || campaigns.length === 0}
            >
              {campaigns.length === 0 && <option value="">No campaigns found</option>}
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
            {(loadingCampaigns || loadingDetail) && <span className="text-xs text-gray-400">Loading…</span>}
          </div>
        </section>

        {snapshot && (
          <>
            <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
              <h2 className="text-lg font-semibold">Campaign Settings</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Campaign Name</label>
                  <input
                    value={snapshot.name}
                    onChange={(e) => updateSnapshot({ name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Timezone</label>
                  <input
                    value={snapshot.timezone}
                    onChange={(e) => updateSnapshot({ timezone: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Start Date</label>
                  <input
                    type="date"
                    value={snapshot.startDate}
                    onChange={(e) => updateSnapshot({ startDate: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">End Date</label>
                  <input
                    type="date"
                    value={snapshot.endDate}
                    onChange={(e) => updateSnapshot({ endDate: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Daily Send Limit</label>
                  <input
                    value={snapshot.dailyLimit}
                    onChange={(e) => updateSnapshot({ dailyLimit: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-400">Email Gap (minutes)</label>
                  <input
                    value={snapshot.emailGapMinutes}
                    onChange={(e) => updateSnapshot({ emailGapMinutes: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <label className="flex items-center gap-2 text-gray-300">
                  <input
                    type="checkbox"
                    checked={snapshot.stopOnReply}
                    onChange={(e) => updateSnapshot({ stopOnReply: e.target.checked })}
                    className="h-4 w-4 accent-red-500"
                  />
                  Stop On Reply
                </label>
                <label className="flex items-center gap-2 text-gray-300">
                  <input
                    type="checkbox"
                    checked={snapshot.openTracking}
                    onChange={(e) => updateSnapshot({ openTracking: e.target.checked })}
                    className="h-4 w-4 accent-red-500"
                  />
                  Open Tracking
                </label>
                <label className="flex items-center gap-2 text-gray-300">
                  <input
                    type="checkbox"
                    checked={snapshot.linkTracking}
                    onChange={(e) => updateSnapshot({ linkTracking: e.target.checked })}
                    className="h-4 w-4 accent-red-500"
                  />
                  Link Tracking
                </label>
              </div>
            </section>

            <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Sequence Steps</h2>
                <button onClick={addStep} className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-xs text-white">
                  Add Step
                </button>
              </div>
              <div className="space-y-4">
                {snapshot.steps.map((step, index) => (
                  <div key={step.id} className="rounded-lg border border-gray-800 bg-gray-900/50 p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-white">Step {index + 1}</div>
                      <button
                        onClick={() => removeStep(step.id)}
                        disabled={snapshot.steps.length <= 1}
                        className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-xs text-gray-200 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-gray-400">Subject</label>
                      <input
                        value={step.subject}
                        onChange={(e) => updateStep(step.id, { subject: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-white"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-gray-400">Email Body</label>
                      <textarea
                        value={step.body}
                        onChange={(e) => updateStep(step.id, { body: e.target.value })}
                        rows={6}
                        className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-white"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-gray-400">Email Preview</label>
                      <div className="rounded border border-gray-800 bg-white text-gray-900 p-3 text-sm max-h-72 overflow-auto">
                        {step.body.trim() ? (
                          <div dangerouslySetInnerHTML={{ __html: toPreviewHtml(step.body) }} />
                        ) : (
                          <span className="text-gray-500">(No body)</span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1 w-64">
                      <label className="text-xs text-gray-400">Days Before Sending This Step</label>
                      <input
                        value={String(step.delayDays)}
                        onChange={(e) => updateStep(step.id, { delayDays: Math.max(0, Number(e.target.value) || 0) })}
                        className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-white"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-3">
              <h2 className="text-lg font-semibold">Sequence Review</h2>
              <p className="text-xs text-gray-400">Current in-editor sequence preview before saving.</p>
              <div className="space-y-2">
                {snapshot.steps.map((step, idx) => (
                  <div key={`preview-${step.id}`} className="rounded border border-gray-800 bg-gray-900/50 px-3 py-2">
                    <div className="text-xs text-gray-400">Step {idx + 1} • Delay {step.delayDays} day(s)</div>
                    <div className="text-sm text-white font-semibold mt-1">{step.subject || '(No subject)'}</div>
                    <div className="rounded border border-gray-800 bg-white text-gray-900 p-3 text-sm mt-2 max-h-72 overflow-auto">
                      {step.body.trim() ? (
                        <div dangerouslySetInnerHTML={{ __html: toPreviewHtml(step.body) }} />
                      ) : (
                        <span className="text-gray-500">(No body)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={!canSave || saving}
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save Campaign Changes'}
              </button>
              <span className="text-xs text-gray-400">Campaign id: {snapshot.id}</span>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
